import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { KeyRound, ShieldCheck, ScrollText, Lock } from 'lucide-react';

const principles = [
  {
    icon: KeyRound,
    title: 'Least-privilege access',
    description:
      'Infrastructure permissions are designed around least privilege — DeploAI should only be able to do what it is explicitly granted access to do.',
  },
  {
    icon: Lock,
    title: 'Human-controlled automation',
    description:
      'Any automated action is intended to run within explicit permissions and controls, not silently in the background.',
  },
  {
    icon: ScrollText,
    title: 'Auditability',
    description:
      'Operational actions and AI recommendations are meant to be traceable, so teams can see what happened and why.',
  },
  {
    icon: ShieldCheck,
    title: 'Deployment safety',
    description:
      'Deployment and infrastructure changes are designed to go through the same guardrails your team already trusts.',
  },
];

const Trust: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="trust" className="py-32 relative overflow-hidden bg-dark-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-semibold mb-6">
            <span className="text-white">Built with</span>{' '}
            <span className="gradient-text">operational trust in mind</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            DeploAI operates infrastructure, so access control, authentication, authorization,
            and auditability are core design considerations — not an afterthought.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10"
        >
          {principles.map((principle) => (
            <div key={principle.title} className="glass rounded-2xl p-8 flex items-start space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-neon-blue rounded-xl flex items-center justify-center flex-shrink-0">
                <principle.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">{principle.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{principle.description}</p>
              </div>
            </div>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center text-gray-500 text-sm max-w-2xl mx-auto"
        >
          DeploAI does not currently hold SOC 2, ISO 27001, or other third-party compliance
          certifications. This section describes the architecture we are designing toward, not a
          certified compliance posture.
        </motion.p>
      </div>
    </section>
  );
};

export default Trust;
